import React, { useState } from 'react';

import styled, { css } from 'styled-components';

import { EllipsesIcon } from 'src/app/components/icons';
import { IconButton } from 'src/app/components/lib/icon-button';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { RebrandOverflowMenu } from 'src/app/components/lib/overflow-menu/overflow-menu-rebrand.styles';
import { useDarkMode } from 'src/app/state/dark-mode';

import type { Action } from 'src/app/layout/main/actions/actions-menu';

export type FormActionEllipsisProps = {
  actions: Action[];
  loading?: boolean;
  useRectangularStyle?: boolean;
};

export function FormActionEllipsis({ actions, loading, useRectangularStyle = false }: FormActionEllipsisProps) {
  const darkMode = useDarkMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionOnClick = (action: Action) => {
    action.onClick();
    handleClose();
  };

  return actions.length === 0 ? null : (
    <>
      <StyledIconButton
        $darkMode={darkMode}
        $useRectangularStyle={useRectangularStyle}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        data-testid='user-row-actions-button'
        disabled={loading}
        onClick={handleClick}
      >
        <EllipsesIcon />
      </StyledIconButton>
      <RebrandOverflowMenu
        $darkMode={darkMode}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      >
        {actions.map((action) =>
          action.visible ? (
            <MenuItem key={action.label} value={action.label} onClick={() => handleActionOnClick(action)}>
              {action.label}
            </MenuItem>
          ) : null
        )}
      </RebrandOverflowMenu>
    </>
  );
}

const StyledIconButton = styled(IconButton)<{ $darkMode: boolean; $useRectangularStyle: boolean }>`
  width: 28px;
  height: 28px;
  padding: 0;
  transition: all 0.2s ease;

  svg {
    width: var(--sizes-40);
  }

  :hover {
    background-color: var(--color-gray-40);
  }

  ${({ $darkMode }) =>
    $darkMode &&
    css`
      :hover {
        background-color: var(--color-gray-80);
      }
      svg {
        ellipse {
          fill: var(--color-brand-primary-white);
        }
      }
    `}

  ${({ $useRectangularStyle, $darkMode }) =>
    $useRectangularStyle &&
    css`
      border: 1px solid var(--color-gray-30);
      border-radius: 4px;

      :hover {
        border-color: var(--color-gray-50);
        background-color: transparent;
      }

      ${$darkMode &&
      css`
        border-color: var(--color-gray-70);

        :hover {
          border-color: var(--color-gray-90);
          background-color: transparent;
        }
      `}
    `}
`;
